import React, { useState } from "react";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";

export default function Contact({ lang, t }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setStatus("error");
        setErrorMessage(errData.message || t("contact.error"));
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setErrorMessage(t("contact.error"));
    }
  };

  return (
    <section id="contact" className="relative w-full min-h-screen bg-zinc-100 font-sans py-16 md:py-24 px-6 md:px-12 flex flex-col justify-between border-b border-zinc-200">
      <FadeIn className="max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-start my-auto">
        {/* Left Column: Contact Info */}
        <div className="lg:col-span-5 space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy tracking-tight font-display">
              {t("contact.title")}
            </h2>
            <div className="h-1 w-16 bg-brand-orange"></div>
            <p className="text-sm text-brand-orange font-medium uppercase tracking-wide">
              {t("contact.subtitle")}
            </p>
          </div>

          <div className="bg-white border border-zinc-200 p-6 shadow-sm space-y-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-navy"></div>
            
            <div className="space-y-3">
              <h3 className="font-bold text-brand-navy text-sm uppercase tracking-wider border-b border-zinc-100 pb-2 font-display">
                {t("contact.officeInfo")}
              </h3>

              <address className="not-italic leading-relaxed space-y-1.5 text-zinc-500 font-light text-xs md:text-sm">
                <div className="font-bold text-brand-navy text-sm mb-1 font-display">{t("contact.address")}</div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-location-dot text-brand-orange text-xs shrink-0 w-3.5" aria-hidden="true" />
                  <span>{t("contact.street")}, {t("contact.city")}, {t("contact.country")}</span>
                </div>
                <div className="flex items-center gap-2.5 text-zinc-400 text-xs pt-1">
                  <i className="fa-solid fa-id-card text-zinc-400 text-xs shrink-0 w-3.5" aria-hidden="true" />
                  <span>{t("contact.cvr")}</span>
                </div>
              </address>
            </div>

            <div className="space-y-2 pt-4 border-t border-zinc-100 text-xs md:text-sm text-zinc-600">
              <div className="flex justify-between items-center bg-zinc-50 p-2.5 border border-zinc-200/60">
                <span className="font-semibold text-zinc-400 text-[10px] uppercase tracking-wider">{t("contact.phoneLabel")} (US):</span>
                <span className="whitespace-nowrap font-bold text-brand-blue hover:text-brand-navy transition-colors duration-200">
                  <a href={`tel:${t("contact.phoneVal").replace(/[^0-9+]/g, "")}`} className="flex items-center gap-1.5" aria-label="Ring til USA kontor">
                    <i className="fa-solid fa-phone text-[10px]" aria-hidden="true" />
                    {t("contact.phoneVal")}
                  </a>
                </span>
              </div>
              <div className="flex justify-between items-center bg-zinc-50 p-2.5 border border-zinc-200/60">
                <span className="font-semibold text-zinc-400 text-[10px] uppercase tracking-wider">{t("contact.phoneLabel")} (DK):</span>
                <span className="whitespace-nowrap font-bold text-brand-blue hover:text-brand-navy transition-colors duration-200">
                  <a href="tel:+4520502788" className="flex items-center gap-1.5" aria-label="Ring til Danmark kontor">
                    <i className="fa-solid fa-phone text-[10px]" aria-hidden="true" />
                    +45 20 50 27 88 (DK)
                  </a>
                </span>
              </div>
              <div className="flex justify-between items-center bg-zinc-50 p-2.5 border border-zinc-200/60">
                <span className="font-semibold text-zinc-400 text-[10px] uppercase tracking-wider">{t("contact.emailLabel")}:</span>
                <span className="whitespace-nowrap font-bold text-brand-blue hover:text-brand-navy transition-colors duration-200">
                  <a href={`mailto:${t("contact.emailVal")}`} className="flex items-center gap-1.5" aria-label="Send e-mail til Albertsen Group">
                    <i className="fa-solid fa-envelope text-[10px]" aria-hidden="true" />
                    {t("contact.emailVal")}
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Contact Form */}
        <div className="lg:col-span-7 bg-white border border-zinc-200 p-6 md:p-8 shadow-sm relative">
          <div className="absolute top-0 left-0 w-1.5 h-full bg-brand-orange"></div>
          <h3 className="text-xl font-bold text-brand-navy mb-6 uppercase tracking-tight font-display border-b border-zinc-100 pb-3">
            {t("contact.formTitle")}
          </h3>

          {status === "success" ? (
            <div className="bg-green-50 border-l-4 border-brand-green p-4 text-brand-green font-medium text-xs animate-fade-in">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-circle-check text-sm" aria-hidden="true" />
                <span>{t("contact.success")}</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Antispam Honeypot Field - Absolutely Hidden */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Website URL</label>
                <input
                  id="website_url"
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Name Field */}
              <div className="space-y-1">
                <label htmlFor="name" className="block text-[10px] font-bold uppercase tracking-[0.15em] text-brand-navy">
                  {t("contact.name")} <span className="text-brand-orange">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-zinc-800 focus:bg-white focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/20 focus:outline-none transition-all duration-200 rounded-none text-xs"
                  placeholder="John Doe"
                />
              </div>

              {/* Phone Field */}
              <div className="space-y-1">
                <label htmlFor="phone" className="block text-[10px] font-bold uppercase tracking-[0.15em] text-brand-navy">
                  {t("contact.phone")} <span className="text-brand-orange">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-zinc-800 focus:bg-white focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/20 focus:outline-none transition-all duration-200 rounded-none text-xs"
                  placeholder="+45 12 34 56 78"
                />
              </div>

              {/* Email Field */}
              <div className="space-y-1">
                <label htmlFor="email" className="block text-[10px] font-bold uppercase tracking-[0.15em] text-brand-navy">
                  {t("contact.email")} <span className="text-brand-orange">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-zinc-800 focus:bg-white focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/20 focus:outline-none transition-all duration-200 rounded-none text-xs"
                  placeholder="john@example.com"
                />
              </div>

              {/* Description Field */}
              <div className="space-y-1">
                <label htmlFor="description" className="block text-[10px] font-bold uppercase tracking-[0.15em] text-brand-navy">
                  {t("contact.description")} <span className="text-brand-orange">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="3"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-zinc-800 focus:bg-white focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/20 focus:outline-none transition-all duration-200 rounded-none text-xs resize-none"
                  placeholder="Please describe your inquiry..."
                />
              </div>

              {/* Error Message */}
              {status === "error" && (
                <div className="bg-red-50 border-l-4 border-brand-red p-3 text-brand-red font-medium text-xs animate-fade-in flex items-center gap-2">
                  <i className="fa-solid fa-circle-exclamation text-sm" aria-hidden="true" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-brand-navy hover:bg-brand-blue disabled:bg-zinc-400 text-white font-bold text-xs uppercase tracking-widest py-3.5 px-5 transition-all duration-200 cursor-pointer rounded-none select-none shadow-sm hover:shadow-md flex items-center justify-center gap-2"
              >
                {status === "loading" ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                    <span>{t("contact.submitting")}</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-paper-plane text-[10px]" aria-hidden="true" />
                    <span>{t("contact.submit")}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </FadeIn>

      {/* Footer integrated at the bottom of the contact slide */}
      <div className="w-full pt-6 border-t border-zinc-200">
        <Footer t={t} />
      </div>
    </section>
  );
}
